import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

// ── Interfaces ────────────────────────────────────────────────

interface InscriptionOption {
  id: string;
  label: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

interface EditingState {
  id: string;
  label: string;
  active: boolean;
  sortOrder: number;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.9rem',
    color: TEXT_DARK,
  },
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1,
  },
  label: {
    fontWeight: 500,
  },
  badgeActive: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#dcfce7',
    color: '#16a34a',
  },
  badgeInactive: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  sortOrder: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    minWidth: '3rem',
    textAlign: 'right' as const,
  },
  actions: {
    display: 'flex',
    gap: '0.25rem',
    marginLeft: '0.75rem',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    transition: 'background-color 0.15s',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.82rem',
    padding: '0.4rem 0.75rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginTop: '0.25rem',
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    alignItems: 'flex-end',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  fieldLabel: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: TEXT_MUTED,
  },
  input: {
    padding: '0.45rem 0.6rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.88rem',
    color: TEXT_DARK,
    outline: 'none',
    backgroundColor: BG_PAGE,
  },
  inputSmall: {
    padding: '0.45rem 0.6rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.88rem',
    color: TEXT_DARK,
    outline: 'none',
    backgroundColor: BG_PAGE,
    width: '4rem',
  },
  checkbox: {
    marginRight: '0.3rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
    color: TEXT_DARK,
    cursor: 'pointer',
    paddingBottom: '0.1rem',
  },
  submitBtn: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'opacity 0.15s',
  },
  editRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    alignItems: 'center',
    padding: '0.5rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_PAGE,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: TEXT_MUTED,
    fontSize: '0.88rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function InscriptionManagement() {
  const [options, setOptions] = useState<InscriptionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteErrorId, setDeleteErrorId] = useState<string | null>(null);

  // Add form state
  const [newLabel, setNewLabel] = useState('');
  const [newActive, setNewActive] = useState(true);
  const [newSortOrder, setNewSortOrder] = useState('0');
  const [submitting, setSubmitting] = useState(false);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient<InscriptionOption[]>(
        '/weekend-booklet/inscription-options',
      );
      setOptions(data);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // ── CRUD Handlers ─────────────────────────────────────────

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel.trim() || submitting) return;

    setSubmitting(true);
    try {
      await apiClient('/weekend-booklet/inscription-options', {
        method: 'POST',
        body: {
          label: newLabel.trim(),
          active: newActive,
          sortOrder: parseInt(newSortOrder, 10) || 0,
        },
      });
      setNewLabel('');
      setNewActive(true);
      setNewSortOrder('0');
      await fetchOptions();
    } catch {
      // Silently handle — could add error state if needed
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    if (!editing || !editing.label.trim() || submitting) return;

    setSubmitting(true);
    try {
      await apiClient(`/weekend-booklet/inscription-options/${editing.id}`, {
        method: 'PATCH',
        body: {
          label: editing.label.trim(),
          active: editing.active,
          sortOrder: editing.sortOrder,
        },
      });
      setEditing(null);
      await fetchOptions();
    } catch {
      // Silently handle
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteError(null);
    setDeleteErrorId(null);
    try {
      await apiClient(`/weekend-booklet/inscription-options/${id}`, {
        method: 'DELETE',
      });
      await fetchOptions();
    } catch (err: any) {
      const message =
        err?.message ||
        'Cannot delete: this inscription option is referenced by existing submissions.';
      setDeleteError(message);
      setDeleteErrorId(id);
    }
  };

  const startEdit = (option: InscriptionOption) => {
    setEditing({
      id: option.id,
      label: option.label,
      active: option.active,
      sortOrder: option.sortOrder,
    });
    setDeleteError(null);
    setDeleteErrorId(null);
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return <div style={styles.loading}>Loading inscription options…</div>;
  }

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Inscription Options</h4>

      {options.length === 0 ? (
        <div style={styles.emptyState}>
          No inscription options yet. Add one below.
        </div>
      ) : (
        <ul style={styles.list} aria-label="Inscription options list">
          {options.map((option) => {
            const isEditing = editing?.id === option.id;

            if (isEditing) {
              return (
                <li key={option.id} style={styles.editRow}>
                  <input
                    style={{ ...styles.input, flex: 1, minWidth: '10rem' }}
                    type="text"
                    value={editing.label}
                    onChange={(e) =>
                      setEditing({ ...editing, label: e.target.value })
                    }
                    aria-label="Edit label"
                  />
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={editing.active}
                      onChange={(e) =>
                        setEditing({ ...editing, active: e.target.checked })
                      }
                    />
                    Active
                  </label>
                  <input
                    style={styles.inputSmall}
                    type="number"
                    value={editing.sortOrder}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        sortOrder: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    aria-label="Edit sort order"
                  />
                  <button
                    style={styles.submitBtn}
                    onClick={handleEditSave}
                    disabled={submitting || !editing.label.trim()}
                  >
                    Save
                  </button>
                  <button
                    style={styles.iconBtn}
                    onClick={cancelEdit}
                    title="Cancel"
                    aria-label="Cancel editing"
                  >
                    ✕
                  </button>
                </li>
              );
            }

            return (
              <React.Fragment key={option.id}>
                <li style={styles.listItem}>
                  <div style={styles.itemInfo}>
                    <span style={styles.label}>{option.label}</span>
                    <span
                      style={
                        option.active ? styles.badgeActive : styles.badgeInactive
                      }
                    >
                      {option.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <span style={styles.sortOrder}>#{option.sortOrder}</span>
                  <div style={styles.actions}>
                    <button
                      style={styles.iconBtn}
                      onClick={() => startEdit(option)}
                      title="Edit"
                      aria-label={`Edit ${option.label}`}
                    >
                      ✏️
                    </button>
                    <button
                      style={styles.iconBtn}
                      onClick={() => handleDelete(option.id)}
                      title="Delete"
                      aria-label={`Delete ${option.label}`}
                    >
                      🗑️
                    </button>
                  </div>
                </li>
                {deleteErrorId === option.id && deleteError && (
                  <li style={{ listStyle: 'none' }}>
                    <div style={styles.error}>{deleteError}</div>
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      )}

      {/* Add form */}
      <form style={styles.form} onSubmit={handleAdd}>
        <div style={styles.fieldGroup}>
          <span style={styles.fieldLabel}>Label</span>
          <input
            style={{ ...styles.input, minWidth: '12rem' }}
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="New inscription option"
            disabled={submitting}
            aria-label="New option label"
          />
        </div>
        <div style={styles.fieldGroup}>
          <span style={styles.fieldLabel}>Active</span>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              style={styles.checkbox}
              checked={newActive}
              onChange={(e) => setNewActive(e.target.checked)}
              disabled={submitting}
            />
            Active
          </label>
        </div>
        <div style={styles.fieldGroup}>
          <span style={styles.fieldLabel}>Sort Order</span>
          <input
            style={styles.inputSmall}
            type="number"
            value={newSortOrder}
            onChange={(e) => setNewSortOrder(e.target.value)}
            disabled={submitting}
            aria-label="New option sort order"
          />
        </div>
        <button
          type="submit"
          style={{
            ...styles.submitBtn,
            opacity: !newLabel.trim() || submitting ? 0.6 : 1,
          }}
          disabled={!newLabel.trim() || submitting}
        >
          Add Option
        </button>
      </form>
    </div>
  );
}
